import template from "./templates/designer.tmpl";
import helpInfoTemplate from "./templates/help-info-control.tmpl";
import Canvas from "../../core/canvas/Canvas";
import GalleryManager from "../../core/gallery/GalleryManager";

import localize from "../../core/localize";
import Upload from "../../core/lib/Upload";
import AppSettings from "../../core/AppSettings";
import * as api from "../../core/net/api";
import { LAYER_TYPE } from "../../core/constants";
import { SIGNATURE } from "../../core/canvas/itemTypes";
import Loader from "../../core/skins/Core_Metal/Loader";
import CustomError from "./CustomError";
import Preview from "./Preview";
import ActiveDesignerView from "./components/ActiveDesignerView";
import HelpInfo from "./components/HelpInfo";
import SvgControl from "../../core/skins/Core_Metal/components/SvgControl";

import POTRACE from "potrace";
import cursor from "./assets/images/eraser.cur";
import { METAL_FEATURES } from "../../core/skins/Core_Metal/core/constants";
import CanvasUtils from "../../core/skins/Core_Metal/components/CanvasUtils";
import SignatureUtils from "../../core/skins/Core_Metal/components/SignatureUtils";
import Utils from "../../core/skins/Core_Metal/components/Utils";

class Designer {
  constructor(parentElement) {
    // Assigning the HTML template
    this.parentElement = parentElement;
    this.renderedTemplate = template;
    this.parentElement.innerHTML = this.renderedTemplate;
    this.designerRootClass = ".aam-designer-init";

    // Element variables declaration
    this.page_el = this.parentElement;
    this.el = this.parentElement.querySelector(".designer");
    this.el.insertAdjacentHTML("beforeBegin", helpInfoTemplate);
    this.canvasContainer = this.el.querySelector(
      "#aam-designer .canvas-container"
    );
    this.canvasFigureContainer = this.el.querySelector(
      "#aam-designer .template-figure"
    );

    this.uploadError = this.el.querySelector("#upload-error");
    this.confirmBtn = this.el.querySelector(".confirm--button .btn");
    this.defaultNumberToDelete = 40;

    // Variables declaration
    this.galleryManager = new GalleryManager();
    this.myListener = this.showUploadError.bind(this);
    this.uploadResponse = "";
    this.svgOriginal = "";
    this.svgOptimised = "";
    this.optimisedStrokeSign = "";
    this.optimisedSolidSign = "";
    this.initialOrgSvg = "";
    this.initialOptimisedSvg = "";
    this.isSvgConverted = false;
    this.trimmedSignDimensions = "";
    this.template = {
      width:
        AppSettings.designerSettings.Desktop.Right -
          AppSettings.designerSettings.Desktop.Left || 241,
      height:
        AppSettings.designerSettings.Desktop.Bottom -
          AppSettings.designerSettings.Desktop.Top || 153,
      initialSize: {
        width:
          AppSettings.designerSettings.Desktop.Right -
            AppSettings.designerSettings.Desktop.Left || 241,
        height:
          AppSettings.designerSettings.Desktop.Bottom -
            AppSettings.designerSettings.Desktop.Top || 153,
      },
    };

    // initialize Canvas dimensions
    /* specifying smaller dimensions for mobile devices */
    const init_elem = document.querySelector(".aam-designer-init");
    if (
      init_elem.classList.contains("mobile") &&
      (window.innerWidth <= 690 || window.innerHeight <= 690)
    ) {
      this.canvasWidth = 280;
      this.el.querySelector('.canvas-container').style.maxWidth = 280 + 'px';
    } else {
      this.canvasWidth = 600;
      this.el.querySelector('.canvas-container').style.maxWidth = 600 + 'px';
    }

    const ratio = this.template.width / this.template.height;
    this.canvasHeight = Math.floor(this.canvasWidth / ratio);
    
    /* Assign width & height to main canvas wrapping div for exact calculations */
    this.el.querySelector('.canvas-inner-container').style.maxWidth = this.canvasWidth + 'px';
    this.el.querySelector('.canvas-inner-container').style.height = this.canvasHeight + 'px';

    // Loader
    this.loader = new Loader(this.page_el);
    this.loader.show();    

    // Image uploading Event
    this.renderCardUploader();

    // Append the template as image behind the Canvas
    const templateImg = document.createElement("IMG");
    templateImg.src = AppSettings.designerSettings.DesignTemplate.UrlLarge;
    templateImg.id = AppSettings.designerSettings.DesignTemplate.Id;
    this.canvasFigureContainer.appendChild(templateImg);

    // Add class for each view
    this.currentViewPage = new ActiveDesignerView();
    this.currentViewPage.setActiveView("view-landing-wrapper");

    // Handle Popup Info for each page
    const helpInfo = new HelpInfo();

    // Canvas render here
    this.canvas = new Canvas("#ssg-canvas", {
      width: this.canvasWidth,
      height: this.canvasHeight,
      imageId: AppSettings.designerSettings.DesignTemplate.Id,
      imageUrl: AppSettings.designerSettings.DesignTemplate.UrlLarge,
      onCardCoverage: (e) => this.handleTestCoverage(e),
      touchDevice: AppSettings.isTouchDevice(),
      isResponsible: false, //this setting is used for changing the size of canvas on resize which is not required in Metal as the Canvas size is fixed always.
      skinName: "Metal",
      onLoaded: async () => {
        this.loader.hide();
        this.showDesignerView();
        AppSettings.isCustomImage = false;
      },
    });

    // init designer
    this.initDesginer();
  }

  initDesginer() {
    this.svgControl = new SvgControl();

    this.canvasUtils = new CanvasUtils(this.canvas, this.el);

    this.signatureUtils = new SignatureUtils(this.el);
    this.utils = new Utils();
    this.defaultSignatureHandler();

    // language supports for all the parent elements
    localize(this.parentElement, AppSettings.Language);

    // Error : if signature on card is not enabled
    if (!AppSettings.isSignatureEnabled()) {
      // Error Page
      const signatureErrorMessage =
        AppSettings.Language.Administration.assignSignature ||
        "Administration.assignSignature";
      new CustomError(this.parentElement, signatureErrorMessage, {
        errorTitle:
          AppSettings.Language.Administration.errorTitle ||
          "Administration.errorTitle",
      });
    }

    // Optimisation Popup handler from desinger page 
    this.signatureUtils.showOptimisationPopup(this.currentViewPage);
  }

  defaultSignatureHandler() {
    const signatureCoverage = this.canvas.getSignatureCoverageArea();
    // Add signature to canvas
    const $defaultSignatureCanvas = document.getElementById("defaultSignatureCanvas");
    $defaultSignatureCanvas.width = this.canvasWidth;
    $defaultSignatureCanvas.height = this.canvasHeight;
    this.signatureUtils.applyDefaultSignature(
      signatureCoverage,
      $defaultSignatureCanvas
    );
  }

  showUploadError(visible) {
    if (visible == true) {
      this.uploadError.classList.add("show");
    } else {
      this.uploadError.classList.remove("show");
      this.el.removeEventListener("click", this.myListener, false);
    }
  }

  showDesignerView() {
    this.canvasUtils.showCanvasInCameo(true);
    this.el.classList.remove("invisible");
    this.canvas.setActiveMode(SIGNATURE); //Adding as we dont want the card to move or scale (need better method)
    this.manageBodyScroll();
  }

  // To apply body scroll dynamically if content's height increases than device height
  manageBodyScroll() {
    const windowHeight = window.innerHeight;
    const aamHeight = document.querySelector("#aam-designer").offsetHeight;

    if (aamHeight > windowHeight) {
      document.querySelector("#aam-designer").classList.add("scrollable");
    }
  }

  // result is true if card coverage is ok
  handleTestCoverage(result) {
    const selectedObject = this.canvas.getSelectedObject();
    if (!result.errorCoverage) {
      if (selectedObject.type === SIGNATURE) {
        switch (AppSettings.designerSettings.Coverage.Signature.Type) {
          case "Error":
            this.canvasUtils.showCoverageError(true);
            this.confirmBtn.classList.add("disabled");
            break;
          case "Warning":
            this.canvasUtils.showCoverageError(true);
            this.confirmBtn.classList.remove("disabled");
            break;
          default:
        }
      }
    } else {
      this.canvasUtils.hideCoverageError();
    }
    this.canvasUtils.showSizeError(!result.warningSize);
  }

  renderCardUploader() {
    const uploadInfo = this.el.querySelector(
      ".toolbox--image-selection--upload--info"
    );

    // Upload core button
    this.el.querySelector("#btn-card-image-upload").onclick = () => {
      input.click();
    };

    // upload another signature button on optimisation view
    if (this.el.querySelector("#upload-another-pic-popup") != null) {
      this.el.querySelector("#upload-another-pic-popup").onclick = () => {
        this.canvas.selectItems();
        input.click();
      };
    }
    
    // Confirm button on optimisation view
    this.el.querySelector("#btn-optimisation-confirm").onclick = () => {
      this.canvas.clearSignature();
      this.currentViewPage.setActiveView("view-preview-wrapper");
      this.setOptimisedSignatures();
    };

    const onProgress = ({ percentComplete }) => {
      if (percentComplete) {
        uploadInfo.innerHTML =
          (AppSettings.Language.uploadingText || "uploadingText") +
          ` ${percentComplete} %`;
      }
    };

    const onComplete = async (res) => {
      AppSettings.isCustomImage = true;
      uploadInfo.innerHTML = "";
      this.uploadResponse = res;

      this.loader.hide();
      this.showUploadError(false);

      this.canvas.setActiveMode(SIGNATURE);
      this.SVGSignatureHandler(res);

      this.currentViewPage.setActiveView("view-svgOptimisation-wrapper");
    };

    //if apiError, returns error from api response
    const onError = (errStr, apiError) => {
      const errorText =
        errStr ||
        AppSettings.Language.uploadErrors.error ||
        "uploadErrors.error";
      this.uploadError.innerHTML = errorText;
      this.loader.hide();
      this.showUploadError(true);

      this.el.addEventListener("click", this.myListener, false);
    };

    const input = this.el.querySelector("#file-upload");

    new Upload(
      api.getImageUploadUrl(
        AppSettings.handoverKey,
        AppSettings.clientId,
        LAYER_TYPE.CARD
      ),
      input,
      {
        autoUpload: true,
        callbacks: {
          onComplete,
          onError,
          onProgress,
          onSubmit: () => this.loader.show(),
        },
      }
    );
  }

  // Updating signature variables with deleted paths
  setOptimisedSignatures() {
    this.loader.show();
    this.optimisedStrokeSign = this.el.querySelector("#renderedSVG").innerHTML; //returns string
    const optimisedStrokeSignHtml = this.el.querySelector("#renderedSVG").children[0]; //returns html

    // **This is a test to show that after rotation, image gets cut (hR sign)
    // let c = oc.toDataURL();
    // let x = document.createElement('IMG');
    // x.src = c;
    // x.id = 'test';
    // document.body.appendChild(optimisedStrokeSignHtml);

    const optimisedStrokeSignHtml_cln = optimisedStrokeSignHtml.cloneNode(true);

    // Updating solid svg variable after deletion of paths in stroke svg
    const paths = optimisedStrokeSignHtml_cln.querySelectorAll("path");
    paths.forEach(function (path) {
      path.setAttribute("fill", "black");
      path.setAttribute("stroke", "none");
      path.setAttribute("stroke-width", "0");
    });
    const optimisedSolidSignHtml = optimisedStrokeSignHtml_cln; //returns html

    this.initiateTrimming(optimisedSolidSignHtml);
  }

  // Updating signature variables after trimming the blank area
  initiateTrimming(optimisedSolidSignHtml) {
    // create a temp. canvas to trim solid svg
    const oc = document.createElement("canvas"),
      octx = oc.getContext("2d");

    // Convert solid svg to base64 to put onto trimming-canvas
    const solid_myImg = new Image();
    solid_myImg.crossOrigin = "Anonymous";
    const solid_xml = new XMLSerializer().serializeToString(optimisedSolidSignHtml);
    const solid_svg64 = btoa(solid_xml);
    const solid_b64Start = "data:image/svg+xml;base64,";
    const solid_image64 = solid_b64Start + solid_svg64;
    solid_myImg.src = solid_image64;
    solid_myImg.id = "solidSignToSubmit";

    // Apply Canvas-size limitations by fetching the allowed canvas size
    solid_myImg.onload = function () {
      checkCanvasSize();
    }

    const checkCanvasSize = () => {
      // Passing the image dimanesions as the canvas dimensions
      const options_param = {
        initial_w: solid_myImg.width,
        initial_h: solid_myImg.height
      }

      // Fetch Allowed Canvas dimensions
      this.canvas.getAllowedCanvasSize(options_param)
      .then((rev_dimensions) => {
        applyRevisedDimensions(rev_dimensions);
      },
      (error) => {
        console.log(error);
      });
    }

    const applyRevisedDimensions = (rev_dimensions) => {
      // Assigning the updated dimensions to canvas
      oc.width = rev_dimensions.canvasWidth;
      oc.height = rev_dimensions.canvasHeight;
      
      octx.drawImage(
        solid_myImg,
        0,
        0,
        solid_myImg.width,
        solid_myImg.height,
        0,
        0,
        oc.width,
        oc.height
      );
      trimCanvas(oc);
    };

    const trimCanvas = (canvas) => {
      this.trimCanvasHandler(canvas);
    };
  }

  trimCanvasHandler(canvas) {
    // trim and return solid png
    const trimmedSolidPNG = this.canvasUtils.trimCanvas(canvas);

    const trimmed = this.canvasUtils.getTrimmedData();

    // converting trimmedSolidPNG to svg using Potrace
    POTRACE.trace(trimmedSolidPNG, (err, svg) => {
      if (err) throw err;

      // all operations like, saving, trimming, converting into base64 with 2 varient (stroke, solid)
      this.svgControl.svgTrimStrokeSolidOperation(svg);

      // get trimmed solid and stroke svg
      const trimmedSolidSvg64 = this.svgControl.getTrimmedSolidSvg();
      const trimmedStrokeSvg64 = this.svgControl.getTrimmedStrokeSvg();
      
      setTrimmedImages(trimmedSolidSvg64, trimmedStrokeSvg64);
    });

    const setTrimmedImages = async (trimmedSolidSvg64, trimmedStrokeSvg64) => {
      this.optimisedSolidSign = trimmedSolidSvg64; //saving solid base64 svg
      this.optimisedStrokeSign = trimmedStrokeSvg64; //saving stroke base64 svg
      this.trimmedSignDimensions = {
        width: trimmed.width,
        height: trimmed.height,
      };

      // Add Trimmed signature to canvas
      this.canvasUtils.addSignToCanvasHandler(trimmed, this.uploadResponse, this.optimisedStrokeSign, function () {
        showPreviewAfterOptimisation();
      });
    };

    const showPreviewAfterOptimisation = () => {
      this.loader.hide(); //Hide the loader
      this.showPreview();
    }
  }

  showPreview() {
    const { Width, Height } = AppSettings.designerSettings.Printer.Size;

    const img = this.canvas.getPreviewImage(Width, Height);
    const previewImage = new Image();

    if (AppSettings.isCustomImage) {
      this.canvasUtils.showCanvasInCameo(true);
    }

    previewImage.src = img;
    previewImage.onload = () => {
      // prepare final solid sign to submit
      this.canvas.manageSolidImageToSubmit(
        this.optimisedSolidSign,
        this.canvasWidth,
        this.canvasHeight,
        this.trimmedSignDimensions
      );

      new Preview(this.parentElement, previewImage, {
        onClose: () => {
          this.parentElement.querySelector(".designer").classList.remove('hide');
          this.canvasUtils.showCanvasInCameo(true);
        },

        onNext: () => {
          this.showSubmitInfo(true);
          this.loader.show();
          this.submit();
        },
      });
    };
  }

  showSubmitInfo(visible) {
    if (visible == true) {
      const msg =
        '<div class="message-container"><div id="submit-message" class="error-message show">' +
        AppSettings.Language.submitMessage +
        "</div></div>";
      document
        .querySelector(".main-container")
        .insertAdjacentHTML("beforeend", msg);
    }
  }

  submit() {
    // Only for Metal Skin as we need to submit 'processed solid signature image' again
    this.canvasUtils.makeUploadRequest(this.canvas.croppedSignatureImage);
  }

  /* ---------------------------------------------SVG SIGNATURE ----------------------------------- */

  // SVG Singnature Handler
  SVGSignatureHandler(res) {
    const signatureContainer = this.el.querySelector("#signatureContainer");
    const renderedSVG = signatureContainer.querySelector("#renderedSVG");
    renderedSVG.innerHTML = "";

    // Reset isSvgCnverted Flag & Clear original & optimised initial values on new image upload
    this.isSvgConverted = false;
    this.initialOrgSvg = '';
    this.initialOptimisedSvg = '';

    this.svgOptimised = res.result.OptimisedImageUrl;
    this.svgOriginal = res.result.OriginalUrl;

    // Hide Landing page elements & show Optimisation View
    this.el.querySelector('.landing-page').classList.add('hide');
    signatureContainer.classList.remove('hide');

    this.SVGEnhancementsInit();

    // toggle controls
    this.el.querySelector("#default").onclick = (e) => {
      this.toggleActiveCheckbox("default");
      this.svgEmbedHandler(
        this.svgOriginal,
        METAL_FEATURES.SIGNATURE.TYPE.ORIGINAL
      );
    };

    this.el.querySelector("#optimised").onclick = (e) => {
      this.toggleActiveCheckbox("optimised");
      this.svgEmbedHandler(
        this.svgOptimised,
        METAL_FEATURES.SIGNATURE.TYPE.OPTIMISED
      );
    };

    

    // reset signature
    this.resetSignatuerHandler();
  }

  SVGEnhancementsInit() {
    // toggle signature (on load deafult original)
    this.toggleActiveCheckbox("optimised");
    this.svgEmbedHandler(
      this.svgOptimised,
      METAL_FEATURES.SIGNATURE.TYPE.OPTIMISED
    );

    this.defaultNumberToDelete = 40;
    // rotate controls
    const signRotate = signatureContainer.querySelector("#signRotate");
    signRotate.onclick = () => {
      this.svgControl.rotateSignatureHandler(renderedSVG, "right");
    };

    const signRotateLeft = signatureContainer.querySelector("#signRotateLeft");
    signRotateLeft.onclick = () => {
      this.svgControl.rotateSignatureHandler(renderedSVG, "left");
    };
  }

  // Toggle Checkbox active
  toggleActiveCheckbox(type) {
    signatureContainer
      .querySelectorAll("[name='currentImg']")
      .forEach((input) => {
        input.parentElement.classList.remove("checked");
      });
    signatureContainer
      .querySelector("[id=" + type + "]")
      .parentElement.classList.add("checked");
  }

  // Convert SVG file into Embeded XML
  svgEmbedHandler(svgUrl, svgType) {
    this.loader.show();

    // only interact one time with api
    if (this.isSvgConverted) {
      let svg;

      if (svgType === METAL_FEATURES.SIGNATURE.TYPE.ORIGINAL) {
        svg = this.initialOrgSvg;
      } else {
        svg = this.initialOptimisedSvg;
      }

      // do the base opetation
      this.signatureOperationHandler(svg);
    } else {
      fetch(svgUrl)
        .then((response) => response.text())
        .then((svg) => {
          // save inital svg for reuse
          if (svgType === METAL_FEATURES.SIGNATURE.TYPE.ORIGINAL) {
            this.initialOrgSvg = svg;
          } else {
            this.initialOptimisedSvg = svg;
          }

          // do the base opetation
          this.signatureOperationHandler(svg);

          // check both converted from server
          if (this.initialOrgSvg !== "" && this.initialOptimisedSvg !== "") {
            this.isSvgConverted = true;
          }
        });
    }
  }

  // base operation like (signaute clone, erase, style, render)
  signatureOperationHandler(svg) {
    this.optimisedSolidSign = svg; //saving optimised solid sign from initial stage
    renderedSVG.innerHTML = svg;

    // noise handler
    this.removeNoiseActionController(svg);

    renderedSVG.dataset.degree = 0;
    this.svgControl.eraseSignatureHandler();
    this.loader.hide();

    // dynamic style
    this.svgControl.applyStyleOnEnhancementSignature(renderedSVG);

    this.optimisedStrokeSign = this.el.querySelector("#renderedSVG").innerHTML; //saving optimised stroke sign from initial stage
  }

  // this will control by button for metal
  removeNoiseActionController(svg) {
    // Action to handle noise spots
    const $noiseHighBtn = this.el.querySelector("#noise-high");
    const $noiseLowBtn = this.el.querySelector("#noise-low");
    const defaultNumber = this.defaultNumberToDelete; // default number to delete spots
    let numberToDelete = defaultNumber;
    const step = 10;

    // increase level of noise
    $noiseHighBtn.addEventListener("click", () => {
      // check if disabled, enable it
      if ($noiseLowBtn.getAttribute("disabled")) {
        $noiseLowBtn.removeAttribute("disabled");
      }

      numberToDelete += step;
      this.svgControl.removeSpotsHandler(svg, renderedSVG, numberToDelete);
      // apply erase functionality
      this.svgControl.eraseSignatureHandler();
    });

    // decrease level of noise
    $noiseLowBtn.addEventListener("click", () => {
      if (numberToDelete > 0) {
        numberToDelete -= step;
        this.svgControl.removeSpotsHandler(svg, renderedSVG, numberToDelete);
        // apply erase functionality
        this.svgControl.eraseSignatureHandler();
      } else {
        $noiseLowBtn.setAttribute("disabled", "disabled");
      }
    });
  }

  // reset signature
  resetSignatuerHandler() {
    const signReset = signatureContainer.querySelector("#signReset");
    signReset.addEventListener("click", () => {
      this.SVGEnhancementsInit();
    });
  }

  /* ---------------------------------------------SVG SIGNATURE ----------------------------------- */
}

export default Designer;

// TODO: Code refactor
// - ES6 Based Syntax - Remove all the es5 code including var keywords - DONE
// Portrace will be part of component - (third party stuff)
// todo add breakframe functionality
// todo move routing into MainContainer
// todo realtime image check
// todo  apitracker
